/* eslint-disable no-irregular-whitespace */
/**
 * JSON-LD schema builders for AI/LLM visibility.
 *
 * These feed ChatGPT, Perplexity, Gemini, Claude, Google AI Overviews,
 * Google Rich Results, Bing Chat, and the firm's Knowledge Panel. Edit
 * facts here — every page that renders them picks the change up on
 * next deploy.
 *
 * Rendered via `<JsonLd data={...} />` which emits a plain
 * `<script type="application/ld+json">` tag anywhere in the DOM.
 */

const SITE_ORIGIN = 'https://apply.thelemonpros.com';
const BRAND_ORIGIN = 'https://thelemonpros.com';

// Shared identifiers used across schemas so Google can stitch them into
// a single Knowledge Graph entity. Using @id references keeps the firm,
// attorneys, services, and place as one entity rather than duplicates.
const FIRM_ID        = `${SITE_ORIGIN}/#firm`;
const WEBSITE_ID     = `${SITE_ORIGIN}/#website`;
const SAEEDIAN_ID    = `${SITE_ORIGIN}/#attorney-saeedian`;
const KHOUBIAN_ID    = `${SITE_ORIGIN}/#attorney-khoubian`;
const PLACE_ID       = `${SITE_ORIGIN}/#office`;
const OG_IMAGE       = `${SITE_ORIGIN}/og-image.png`;

// ─── 1 · LegalService (firm identity) ───────────────────────────────
export function legalServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type':    'LegalService',
    '@id':      FIRM_ID,
    name:          'The Lemon Pros',
    legalName:     'The Lemon Pros',
    alternateName: [
      'Lemon Pros',
      'The Lemon Pros Attorneys',
      'The Lemon Law Pros',
      'California Lemon Law Attorneys',
      'The California Lemon Law Attorneys',
    ],
    description:
      "California lemon-law firm representing drivers of defective vehicles under the Song-Beverly Consumer Warranty Act. Manufacturers pay the attorneys' fees — clients recover a buyback, replacement, or cash settlement at $0 out of pocket.",
    url:       `${SITE_ORIGIN}/`,
    logo:      `${SITE_ORIGIN}/lemon-pros-logo-square-1200.png`,
    image:     `${SITE_ORIGIN}/lemon-pros-logo-square-1200.png`,
    telephone: '+1-844-335-8911',
    priceRange: '$0 — No fee unless we win',
    address: {
      '@type':         'PostalAddress',
      streetAddress:   '9100 Wilshire Blvd #500W',
      addressLocality: 'Beverly Hills',
      addressRegion:   'CA',
      postalCode:      '90212',
      addressCountry:  'US',
    },
    location:          { '@id': PLACE_ID },
    areaServed:        { '@type': 'State', name: 'California' },
    availableLanguage: ['English', 'Spanish'],
    serviceType:       ['Lemon Law', 'Consumer Protection', 'Warranty Litigation'],
    knowsAbout: [
      'California Song-Beverly Consumer Warranty Act',
      'Magnuson-Moss Warranty Act',
      'Vehicle buyback',
      'Vehicle replacement',
      'Warranty breach claims',
      'Lemon law for cars, SUVs, trucks, EVs, motorcycles, and RVs',
    ],
    makesOffer: [
      { '@type': 'Offer', itemOffered: { '@id': `${SITE_ORIGIN}/#service-buyback` } },
      { '@type': 'Offer', itemOffered: { '@id': `${SITE_ORIGIN}/#service-replacement` } },
      { '@type': 'Offer', itemOffered: { '@id': `${SITE_ORIGIN}/#service-cash` } },
    ],
    founder: {
      '@type':       'Attorney',
      '@id':         SAEEDIAN_ID,
      name:          'Michael Saeedian',
      honorificSuffix: 'Esq.',
      jobTitle:      'Founding Attorney',
      worksFor:      { '@id': FIRM_ID },
      knowsAbout:    ['California Lemon Law', 'Song-Beverly Consumer Warranty Act', 'Consumer protection litigation'],
    },
    employee: [
      {
        '@type':       'Attorney',
        '@id':         SAEEDIAN_ID,
        name:          'Michael Saeedian',
        honorificSuffix: 'Esq.',
        jobTitle:      'Founding Attorney',
        knowsAbout:    ['California Lemon Law', 'Song-Beverly Consumer Warranty Act'],
        url:           `${SITE_ORIGIN}/attorneys/michael-saeedian`,
      },
      {
        '@type':       'Attorney',
        '@id':         KHOUBIAN_ID,
        name:          'Timan Khoubian',
        honorificSuffix: 'Esq.',
        jobTitle:      'Attorney',
        knowsAbout:    ['California Lemon Law', 'Magnuson-Moss Warranty Act'],
        url:           `${SITE_ORIGIN}/attorneys/timan-khoubian`,
      },
    ],
    openingHours:      'Mo-Su 00:00-23:59',
    aggregateRating: {
      '@type':     'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '2000',
      bestRating:  '5',
    },
    sameAs: [
      BRAND_ORIGIN,
      'https://www.yelp.com/biz/the-lemon-pros-beverly-hills',
      'https://www.google.com/maps/place/The+Lemon+Pros/data=!4m2!3m1!1s0x0:0x2abdba977447ab15',
      'https://www.bing.com/maps?q=The+Lemon+Pros+Beverly+Hills',
    ],
  };
}

// ─── 2 · WebSite (site-level + sitelinks search box) ───────────────
export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type':    'WebSite',
    '@id':      WEBSITE_ID,
    url:        `${SITE_ORIGIN}/`,
    name:       'The Lemon Pros',
    inLanguage: ['en', 'es'],
    publisher:  { '@id': FIRM_ID },
    potentialAction: {
      '@type':       'SearchAction',
      target: {
        '@type':      'EntryPoint',
        urlTemplate:  `${SITE_ORIGIN}/pa?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

// ─── 3 · Place (office location rich card) ──────────────────────────
export function placeSchema() {
  return {
    '@context': 'https://schema.org',
    '@type':    'Place',
    '@id':      PLACE_ID,
    name:       'The Lemon Pros — Beverly Hills Office',
    address: {
      '@type':         'PostalAddress',
      streetAddress:   '9100 Wilshire Blvd #500W',
      addressLocality: 'Beverly Hills',
      addressRegion:   'CA',
      postalCode:      '90212',
      addressCountry:  'US',
    },
    geo: {
      '@type':    'GeoCoordinates',
      latitude:   34.0670,
      longitude:  -118.4036,
    },
    hasMap:     'https://www.google.com/maps/place/9100+Wilshire+Blvd+%23500W,+Beverly+Hills,+CA+90212',
    telephone:  '+1-844-335-8911',
  };
}

// ─── 4 · BreadcrumbList ─────────────────────────────────────────────
// Pass an array of { name, path } ordered from root to current page.
// `path` is a route path like "/pa" or "/attorneys/michael-saeedian".
// The schema always prepends a "Home" root at `/`.
export function breadcrumbsSchema(crumbs = []) {
  const items = [
    { name: 'The Lemon Pros', path: '/' },
    ...crumbs,
  ];
  return {
    '@context': 'https://schema.org',
    '@type':    'BreadcrumbList',
    itemListElement: items.map((c, i) => ({
      '@type':   'ListItem',
      position:  i + 1,
      name:      c.name,
      item:      `${SITE_ORIGIN}${c.path.startsWith('/') ? c.path : '/' + c.path}`,
    })),
  };
}

// ─── 5 · Service (buyback / replacement / cash) ─────────────────────
// Three Offers that AI Overviews surface as service cards. Each is a
// distinct Service entity tied to the firm so a user asking
// "what remedies does lemon law offer?" sees us.
export function serviceBuybackSchema() {
  return {
    '@context': 'https://schema.org',
    '@type':    'Service',
    '@id':      `${SITE_ORIGIN}/#service-buyback`,
    serviceType: 'Vehicle Buyback',
    name:        'California Lemon Law Vehicle Buyback',
    description:
      'The manufacturer repurchases the defective vehicle at the price you paid, minus a statutory usage offset based on the mileage when the defect was first reported.',
    provider:    { '@id': FIRM_ID },
    areaServed:  { '@type': 'State', name: 'California' },
    audience:    { '@type': 'Audience', audienceType: 'California vehicle owners' },
    serviceOutput: 'Full refund of purchase price minus mileage offset.',
  };
}

export function serviceReplacementSchema() {
  return {
    '@context': 'https://schema.org',
    '@type':    'Service',
    '@id':      `${SITE_ORIGIN}/#service-replacement`,
    serviceType: 'Vehicle Replacement',
    name:        'California Lemon Law Vehicle Replacement',
    description:
      'The manufacturer provides a new vehicle of equal value in exchange for the defective one, with warranty terms refreshed from the replacement date.',
    provider:    { '@id': FIRM_ID },
    areaServed:  { '@type': 'State', name: 'California' },
    audience:    { '@type': 'Audience', audienceType: 'California vehicle owners' },
    serviceOutput: 'Equivalent new vehicle at no additional cost to the client.',
  };
}

export function serviceCashSchema() {
  return {
    '@context': 'https://schema.org',
    '@type':    'Service',
    '@id':      `${SITE_ORIGIN}/#service-cash`,
    serviceType: 'Cash Settlement',
    name:        'California Lemon Law Cash Settlement',
    description:
      'The manufacturer pays a lump-sum settlement for the diminished value of the vehicle while the client keeps the car — ideal when the vehicle is still usable despite the defect.',
    provider:    { '@id': FIRM_ID },
    areaServed:  { '@type': 'State', name: 'California' },
    audience:    { '@type': 'Audience', audienceType: 'California vehicle owners' },
    serviceOutput: 'Lump-sum settlement; client retains the vehicle.',
  };
}

// ─── 6 · Page-type helpers (WebPage / AboutPage / ContactPage) ─────
// These classify each URL for crawlers so the engine knows a page is a
// legal document vs. a landing page vs. a contact page. Pass the route
// path, a human title, and the matching crumb trail.
function _basePageSchema(type, { path, name, description, lang = 'en', crumbs }) {
  const url = `${SITE_ORIGIN}${path.startsWith('/') ? path : '/' + path}`;
  const out = {
    '@context':   'https://schema.org',
    '@type':      type,
    '@id':        `${url}#page`,
    url,
    name,
    inLanguage:   lang,
    isPartOf:     { '@id': WEBSITE_ID },
    about:        { '@id': FIRM_ID },
    publisher:    { '@id': FIRM_ID },
  };
  if (description) out.description = description;
  if (crumbs && crumbs.length) {
    out.breadcrumb = breadcrumbsSchema(crumbs);
  }
  return out;
}
export const webPageSchema     = (args) => _basePageSchema('WebPage',     args);
export const aboutPageSchema   = (args) => _basePageSchema('AboutPage',   args);
export const contactPageSchema = (args) => _basePageSchema('ContactPage', args);

// ─── 7 · Attorney Person pages ──────────────────────────────────────
// Deep Person schema per attorney, including education, bar admissions,
// languages, and expertise. These produce rich "Knowledge Panel" cards
// when a user asks "who is Michael Saeedian?".
export function attorneyPersonSchema(slug) {
  if (slug === 'michael-saeedian') {
    return {
      '@context': 'https://schema.org',
      '@type':    'Attorney',
      '@id':      SAEEDIAN_ID,
      name:       'Michael Saeedian',
      honorificSuffix: 'Esq.',
      jobTitle:   'Founding Attorney',
      worksFor:   { '@id': FIRM_ID },
      url:        `${SITE_ORIGIN}/attorneys/michael-saeedian`,
      image:      `${SITE_ORIGIN}/lemon-pros-logo-square-1200.png`,
      knowsLanguage: ['English'],
      knowsAbout: [
        'California Song-Beverly Consumer Warranty Act',
        'Magnuson-Moss Warranty Act',
        'Consumer protection litigation',
        'Vehicle warranty disputes',
        'Automotive manufacturer liability',
      ],
      alumniOf: [
        { '@type': 'CollegeOrUniversity', name: 'Loyola Law School, Los Angeles' },
        { '@type': 'CollegeOrUniversity', name: 'University of California, Los Angeles (UCLA)' },
      ],
      hasCredential: [
        {
          '@type':             'EducationalOccupationalCredential',
          credentialCategory:  'license',
          name:                'State Bar of California — Attorney License',
          identifier:          '265470',
          recognizedBy: { '@type': 'Organization', name: 'State Bar of California' },
          url:                 'https://apps.calbar.ca.gov/attorney/Licensee/Detail/265470',
        },
      ],
      award: [
        'Super Lawyers Rising Stars (2020)',
        'Super Lawyers Rising Stars (2021)',
        'Super Lawyers Rising Stars (2022)',
        'Super Lawyers Rising Stars (2023)',
        'Super Lawyers Rising Stars (2024)',
        'Super Lawyers Rising Stars (2025)',
      ],
      memberOf: {
        '@type': 'Organization',
        name:    'State Bar of California',
      },
    };
  }
  if (slug === 'timan-khoubian') {
    return {
      '@context': 'https://schema.org',
      '@type':    'Attorney',
      '@id':      KHOUBIAN_ID,
      name:       'Timan Khoubian',
      honorificSuffix: 'Esq.',
      jobTitle:   'Attorney',
      worksFor:   { '@id': FIRM_ID },
      url:        `${SITE_ORIGIN}/attorneys/timan-khoubian`,
      image:      `${SITE_ORIGIN}/lemon-pros-logo-square-1200.png`,
      knowsLanguage: ['English'],
      knowsAbout: [
        'California Lemon Law',
        'Magnuson-Moss Warranty Act',
        'Consumer warranty litigation',
        'Vehicle buyback claims',
      ],
      alumniOf: [
        { '@type': 'CollegeOrUniversity', name: 'UCLA School of Law' },
        { '@type': 'CollegeOrUniversity', name: 'UCLA Anderson School of Management' },
        { '@type': 'CollegeOrUniversity', name: 'University of California, Los Angeles (UCLA)' },
      ],
      hasCredential: [
        {
          '@type':             'EducationalOccupationalCredential',
          credentialCategory:  'license',
          name:                'State Bar of California — Attorney License',
          identifier:          '296208',
          recognizedBy: { '@type': 'Organization', name: 'State Bar of California' },
          url:                 'https://apps.calbar.ca.gov/attorney/Licensee/Detail/296208',
        },
        {
          '@type':            'EducationalOccupationalCredential',
          credentialCategory: 'degree',
          name:               'Juris Doctor, UCLA School of Law (2010, with honors, Order of the Coif)',
        },
        {
          '@type':            'EducationalOccupationalCredential',
          credentialCategory: 'degree',
          name:               'MBA, UCLA Anderson School of Management (Carter Fellow)',
        },
      ],
      award: ['Order of the Coif', 'Phi Beta Kappa', 'Summa cum laude (UCLA, 2004)'],
      memberOf: {
        '@type': 'Organization',
        name:    'State Bar of California',
      },
    };
  }
  return null;
}

// ─── 8 · FAQPage (English) ──────────────────────────────────────────
export function faqSchemaEn(pageUrl = `${SITE_ORIGIN}/pa`) {
  return {
    '@context':  'https://schema.org',
    '@type':     'FAQPage',
    '@id':       `${pageUrl}#faq`,
    inLanguage:  'en',
    mainEntity: [
      {
        '@type': 'Question',
        name:    'How many repair attempts before my car qualifies as a lemon in California?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            "Under California's Song-Beverly Consumer Warranty Act, a vehicle may qualify after two or more unsuccessful repair attempts for the same serious safety defect, four or more attempts for the same non-safety defect, or thirty cumulative days out of service while under the original factory warranty.",
        },
      },
      {
        '@type': 'Question',
        name:    'Who pays the attorney in a California lemon law case?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            "The vehicle manufacturer pays. California Civil Code §1794(d) requires the manufacturer to pay the prevailing consumer's reasonable attorneys' fees and costs, so you pay $0 out of pocket when we win and nothing if we don't.",
        },
      },
      {
        '@type': 'Question',
        name:    "What can I recover under California's lemon law?",
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Three remedies: a full buyback (the manufacturer repurchases the vehicle at the price you paid, minus a usage offset), a replacement vehicle of equal value, or a cash settlement for diminished value while you keep the car.',
        },
      },
      {
        '@type': 'Question',
        name:    'Can I still file if my factory warranty has expired?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            "California's statute of limitations is four years from the date you knew or should have known the defect could not be fixed, so claims can often be filed well after the warranty mileage or time window closes — provided the defect first appeared while the warranty was active.",
        },
      },
      {
        '@type': 'Question',
        name:    'Do I need to keep making car payments during a lemon law claim?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes. Keep making every scheduled payment and keep driving the vehicle normally until the case resolves. Missing payments can hurt your credit and complicate the buyback calculation.',
        },
      },
    ],
  };
}

// ─── 9 · FAQPage (Spanish) ──────────────────────────────────────────
export function faqSchemaEs(pageUrl = `${SITE_ORIGIN}/spa`) {
  return {
    '@context':  'https://schema.org',
    '@type':     'FAQPage',
    '@id':       `${pageUrl}#faq`,
    inLanguage:  'es',
    mainEntity: [
      {
        '@type': 'Question',
        name:    '¿Cuántos intentos de reparación necesito para que mi carro califique como limón en California?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Bajo la Ley Song-Beverly de California, un vehículo puede calificar después de dos o más intentos fallidos de reparación del mismo defecto grave de seguridad, cuatro o más intentos del mismo defecto no relacionado con seguridad, o treinta días acumulados sin servicio mientras el vehículo aún está bajo la garantía original de fábrica.',
        },
      },
      {
        '@type': 'Question',
        name:    '¿Quién paga al abogado en un caso de Ley Limón en California?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'El fabricante del vehículo. El Código Civil de California §1794(d) obliga al fabricante a pagar los honorarios y costos razonables del abogado del consumidor que gana el caso, por lo que usted paga $0 de su bolsillo si ganamos y nada si no ganamos.',
        },
      },
      {
        '@type': 'Question',
        name:    '¿Qué puedo recuperar bajo la Ley Limón de California?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Tres remedios: una recompra completa (el fabricante recompra el vehículo al precio que usted pagó, menos un descuento por uso), un vehículo de reemplazo de valor equivalente, o un acuerdo en efectivo por la pérdida de valor si usted conserva el vehículo.',
        },
      },
      {
        '@type': 'Question',
        name:    '¿Puedo presentar un caso si la garantía de fábrica ya venció?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'A menudo sí. El plazo de prescripción en California es de cuatro años desde la fecha en que usted supo (o debió saber) que el defecto no se podía reparar, así que muchas reclamaciones pueden presentarse mucho después de que vence la garantía — siempre que el defecto haya aparecido mientras la garantía estaba activa.',
        },
      },
      {
        '@type': 'Question',
        name:    '¿Debo seguir pagando el carro mientras avanza el caso de Ley Limón?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Sí. Siga haciendo cada pago programado y maneje el vehículo normalmente hasta que el caso se resuelva. Dejar de pagar puede dañar su crédito y complicar el cálculo de la recompra.',
        },
      },
    ],
  };
}

// Convenience: return a single, root-level array of entities a React
// page should drop into one <JsonLd data={...} /> call. Any new page
// using the full identity graph can just pass this.
export function identityGraph() {
  return [
    legalServiceSchema(),
    websiteSchema(),
    placeSchema(),
    serviceBuybackSchema(),
    serviceReplacementSchema(),
    serviceCashSchema(),
  ];
}

export { OG_IMAGE, SITE_ORIGIN };
